<template>
  <div class="page-wrapper">
    <div class="content container-fluid">

      <div class="page-header">
        <div class="row">
          <div class="col-sm-12">
            <h3 class="page-title">Quản lý Giấy tờ Xe & Tài xế</h3>
            <ul class="breadcrumb">
              <li class="breadcrumb-item"><router-link to="/">Dashboard</router-link></li>
              <li class="breadcrumb-item active">Giấy tờ</li>
            </ul>
          </div>
        </div>
      </div>

      <div class="card mb-3">
        <div class="card-body d-flex flex-wrap align-items-center gap-2">
          <div class="ml-auto d-flex align-items-center gap-2">
            <select class="form-control form-control-sm" style="width: auto" v-model.number="pageSize" @change="changeSize">
              <option :value="5">5</option>
              <option :value="10">10</option>
              <option :value="20">20</option>
              <option :value="50">50</option>
            </select>
            <button class="btn btn-primary btn-sm" @click="openCreate">
              <i class="fa fa-plus"></i> Thêm Giấy tờ
            </button>
          </div>
        </div>
      </div>

      <div class="card">
        <div class="card-body p-0">
          <div v-if="isLoading" class="p-4 text-center">
            <div class="spinner-border text-primary" role="status"></div>
            <p class="mt-2 mb-0">Đang tải danh sách giấy tờ...</p>
          </div>

          <div v-else>
            <div v-if="items.length === 0" class="p-4 text-center text-muted">
              Không có giấy tờ nào.
            </div>

            <div v-else class="table-responsive">
              <table class="table table-hover table-striped mb-0">
                <thead class="thead-light">
                  <tr>
                    <th>Ảnh</th>
                    <th>Loại Giấy tờ</th>
                    <th>Tên/Số hiệu</th>
                    <th>Chủ sở hữu</th>
                    <th>Ngày cấp</th>
                    <th>Ngày hết hạn</th>
                    <th>Trạng thái</th>
                    <th class="text-right" style="width: 120px;">Thao tác</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="gt in items" :key="gt.id">
                    <!-- trong template: -->
                    <td class="align-middle">
                      <span v-if="!gt.fileUrl" class="text-muted">—</span>

                      <img
                        v-else-if="isImage(gt)"
                        :src="gt.fileUrl"
                        alt="thumb"
                        class="rounded border"
                        style="width:56px;height:42px;object-fit:cover;cursor:pointer"
                        @click="openInNewTab(gt.fileUrl)"
                      />

                      <button
                        v-else
                        type="button"
                        class="btn btn-sm file-chip"
                        :title="fileLabel(gt)"
                        @click="openInNewTab(gt.fileUrl)"
                      >
                        <i :class="fileIconClass(gt, { fa: 4 })"></i>
                      </button>
                    </td>


                    <td>
                      <strong>{{ gt.tenLoaiGiayToDisplay }}</strong><br>
                      <small class="text-muted">{{ gt.tenHienThi || '...' }}</small>
                    </td>
                    <td>{{ gt.soGiayTo || '-' }}</td>
                    <td>
                      <span v-if="gt.xeId">
                        <i class="fa fa-car mr-1 text-primary"></i> {{ gt.xeBienSo || `Xe ID: ${gt.xeId}` }}
                      </span>
                      <span v-else-if="gt.nguoiDungId">
                        <i class="fa fa-user mr-1 text-success"></i> {{ gt.nguoiDungHoTen || `ND ID: ${gt.nguoiDungId}` }}
                      </span>
                      <span v-else class="text-muted">-</span>
                    </td>
                    <td>{{ formatDate(gt.ngayCap) }}</td>
                    <td :class="{ 'text-danger': isExpired(gt.ngayHetHan), 'text-warning': isNearExpiry(gt.ngayHetHan) }">
                      {{ formatDate(gt.ngayHetHan) || 'Không TH' }}
                      <i v-if="isExpired(gt.ngayHetHan)" class="fa fa-exclamation-circle text-danger ml-1" title="Đã hết hạn"></i>
                      <i v-else-if="isNearExpiry(gt.ngayHetHan)" class="fa fa-exclamation-triangle text-warning ml-1" title="Sắp hết hạn"></i>
                    </td>
                    <td>
                      <span :class="['badge', gt.active ? 'badge-success' : 'badge-secondary']">
                        {{ gt.active ? 'Hiệu lực' : 'Hết hiệu lực' }}
                      </span>
                    </td>
                    <td class="text-right">
                      <button class="btn btn-sm btn-outline-secondary mr-1" @click="openEdit(gt)" title="Chỉnh sửa">
                        <i class="fa fa-pencil"></i>
                      </button>
                      <button class="btn btn-sm btn-outline-danger" @click="confirmDelete(gt)" title="Xóa">
                        <i class="fa fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="p-3 d-flex justify-content-between align-items-center border-top">
              <small class="text-muted">
                Tổng: {{ totalElements }} giấy tờ — Trang {{ currentPage }}/{{ totalPages }}
              </small>
              <ul class="pagination mb-0">
                <li class="page-item" :class="{ disabled: currentPage === 1 }">
                  <a href="#" class="page-link" @click.prevent="goPage(currentPage - 1)">Trước</a>
                </li>
                <li v-for="p in totalPages" :key="p" class="page-item" :class="{ active: p === currentPage }">
                  <a href="#" class="page-link" @click.prevent="goPage(p)">{{ p }}</a>
                </li>
                <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                  <a href="#" class="page-link" @click.prevent="goPage(currentPage + 1)">Sau</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal Thêm/Sửa -->
      <div v-if="showModal" class="modal fade show d-block" tabindex="-1" style="background: rgba(0,0,0,.5);">
        <div class="modal-dialog modal-lg" role="document">
          <div class="modal-content">
            <form @submit.prevent="submitForm">
              <div class="modal-header bg-light">
                <h5 class="modal-title font-weight-bold">
                  <i class="fa mr-2" :class="isEdit ? 'fa-pencil-alt' : 'fa-plus-circle'"></i>
                  {{ isEdit ? 'Cập nhật Giấy tờ' : 'Thêm Giấy tờ mới' }}
                </h5>
                <button type="button" class="close" @click="closeModal"><span>&times;</span></button>
              </div>

              <div class="modal-body">
                <div v-if="formError" class="alert alert-danger py-2">{{ formError }}</div>

                <div class="row">
                  <div class="col-md-6">
                    <div class="form-group">
                      <label>Loại Giấy tờ <span class="text-danger">*</span></label>
                      <select v-model="form.maLoaiGiayTo" class="form-control" required @change="updateTenHienThiDefault">
                        <option :value="null" disabled>-- Chọn loại giấy tờ --</option>
                        <optgroup label="Giấy tờ Xe">
                          <option v-for="lg in loaiGiayToXeOptions" :key="lg.value" :value="lg.value">{{ lg.text }}</option>
                        </optgroup>
                        <optgroup label="Giấy tờ Tài xế">
                          <option v-for="lg in loaiGiayToTaiXeOptions" :key="lg.value" :value="lg.value">{{ lg.text }}</option>
                        </optgroup>
                        <option value="KHAC">Khác</option>
                      </select>
                    </div>

                    <div class="form-group">
                      <label>Chủ sở hữu <span class="text-danger">*</span></label>
                      <div class="input-group">
                        <div class="input-group-prepend">
                          <select class="form-control" v-model="ownerType" style="border-top-right-radius: 0; border-bottom-right-radius: 0;">
                            <option value="xe">Xe</option>
                            <option value="taixe">Tài xế</option>
                          </select>
                        </div>
                        <select v-if="ownerType === 'xe'" v-model="form.xeId" class="form-control" required :disabled="isLoadingOptions">
                          <option :value="null" disabled>{{ isLoadingOptions ? 'Đang tải Xe...' : '-- Chọn Xe --' }}</option>
                          <option v-for="xeOpt in xeOptions" :key="xeOpt.id" :value="xeOpt.id">{{ xeOpt.bienSo }} {{ xeOpt.tenLoaiXe ? `(${xeOpt.tenLoaiXe})` : '' }}</option>
                        </select>
                        <select v-if="ownerType === 'taixe'" v-model="form.nguoiDungId" class="form-control" required :disabled="isLoadingOptions">
                          <option :value="null" disabled>{{ isLoadingOptions ? 'Đang tải Tài xế...' : '-- Chọn Tài xế --' }}</option>
                          <option v-for="txOpt in taiXeOptions" :key="txOpt.id" :value="txOpt.id">{{ txOpt.hoTen }}</option>
                        </select>
                      </div>
                      <div v-if="!isLoadingOptions && ownerType === 'xe' && xeOptions.length === 0" class="text-danger small mt-1">
                        Vui lòng thêm Xe trước.
                      </div>
                      <div v-if="!isLoadingOptions && ownerType === 'taixe' && taiXeOptions.length === 0" class="text-danger small mt-1">
                        Vui lòng thêm Người dùng có chức vụ Tài xế trước.
                      </div>
                    </div>

                    <div class="form-group">
                      <label>Tên hiển thị (Tùy chọn)</label>
                      <input v-model.trim="form.tenHienThi" type="text" class="form-control" placeholder="Ví dụ: Đăng kiểm lần 2">
                    </div>

                    <div class="form-group">
                      <label>File đính kèm (URL)</label>
                      <div class="input-group">
                        <input v-model.trim="form.fileUrl" type="url" class="form-control" placeholder="https://...">
                        <div class="input-group-append">
                          <button type="button" class="btn btn-outline-secondary" @click="openFileManager">
                            <i class="fa fa-folder-open"></i>
                          </button>
                        </div>
                      </div>
                      <small v-if="form.fileUrl" class="text-muted d-block mt-1">
                        <a :href="form.fileUrl" target="_blank" rel="noopener">Mở file</a>
                      </small>
                    </div>

                  </div>

                  <div class="col-md-6">
                    <div class="form-group">
                      <label>Số Giấy tờ/Số Seri</label>
                      <input v-model.trim="form.soGiayTo" type="text" class="form-control">
                    </div>
                    <div class="form-row">
                      <div class="form-group col-md-6">
                        <label>Ngày cấp</label>
                        <input v-model="form.ngayCap" type="date" class="form-control">
                      </div>
                      <div class="form-group col-md-6">
                        <label>Ngày hết hạn</label>
                        <input v-model="form.ngayHetHan" type="date" class="form-control">
                      </div>
                    </div>
                    <div class="form-group">
                      <label>Nơi cấp</label>
                      <input v-model.trim="form.noiCap" type="text" class="form-control">
                    </div>
                    <div class="form-group">
                      <label>Trạng thái</label>
                      <select v-model="form.active" class="form-control">
                        <option :value="true">Còn hiệu lực</option>
                        <option :value="false">Hết hiệu lực / Tạm ngưng</option>
                      </select>
                    </div>
                    <div class="form-group mb-0">
                      <label>Ghi chú</label>
                      <textarea v-model.trim="form.ghiChu" class="form-control" rows="3"></textarea>
                    </div>
                  </div>
                </div>
              </div>

              <div class="modal-footer">
                <button type="button" class="btn btn-outline-secondary" @click="closeModal">Hủy bỏ</button>
                <button type="submit" class="btn btn-primary" :disabled="isLoading">
                  <span v-if="isLoading" class="spinner-border spinner-border-sm"></span>
                  <i v-else class="fa" :class="isEdit ? 'fa-save' : 'fa-plus'"></i>
                  {{ isEdit ? 'Lưu thay đổi' : 'Thêm mới' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <!-- Xác nhận xóa -->
      <div v-if="showDelete" class="modal fade show d-block" tabindex="-1" role="dialog" style="background: rgba(0,0,0,.5);">
        <div class="modal-dialog" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">Xoá Giấy tờ</h5>
              <button type="button" class="close" @click="cancelDelete"><span>&times;</span></button>
            </div>
            <div class="modal-body">
              Bạn chắc chắn muốn xoá giấy tờ <strong>{{ pendingDelete?.tenHienThi || pendingDelete?.tenLoaiGiayToDisplay }}</strong>?
            </div>
            <div class="modal-footer">
              <button class="btn btn-outline-secondary" @click="cancelDelete">Huỷ</button>
              <button class="btn btn-danger" @click="doDelete"><i class="fa fa-trash"></i> Xoá</button>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="docPreview.open"
        class="modal fade show d-block"
        tabindex="-1"
        style="background: rgba(0,0,0,.75);"
        @click="closeDocPreview"
      >
        <div class="modal-dialog modal-xl" role="document" @click.stop>
          <div class="modal-content">
            <div class="modal-header bg-light">
              <h5 class="modal-title">
                Xem trước tệp
                <small class="text-muted ml-2" v-if="docPreview.url">{{ fileExt(docPreview.url) }}</small>
              </h5>
              <div class="btn-group mr-2">
                <a v-if="docPreview.url" :href="docPreview.url" target="_blank" rel="noopener" class="btn btn-sm btn-outline-secondary">
                  <i class="fa fa-external-link-alt"></i> Mở tab mới
                </a>
                <button type="button" class="btn btn-sm btn-outline-secondary" @click="closeDocPreview">
                  <i class="fa fa-times"></i> Đóng
                </button>
              </div>
            </div>

            <div class="modal-body" style="min-height:60vh;">
              <!-- Ảnh -->
              <img
                v-if="isImage(docPreview.url)"
                :src="docPreview.url"
                alt="preview"
                style="max-width:100%;max-height:70vh;object-fit:contain;display:block;margin:0 auto;"
              />

              <!-- PDF -->
              <iframe
                v-else-if="isPdf(docPreview.url)"
                :src="docPreview.url"
                width="100%"
                height="700"
                style="border:0;"
              ></iframe>

              <!-- Không hỗ trợ preview -->
              <div v-else class="text-center text-muted">
                <p class="mb-2">Không hỗ trợ xem trước tệp này.</p>
                <a :href="docPreview.url" target="_blank" rel="noopener" class="btn btn-outline-secondary btn-sm">
                  <i class="fa fa-external-link-alt"></i> Mở tab mới
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- File Manager Modal (bản gốc) -->
      <FileManagerModal
        v-if="showFileManagerModal"
        :folderTabs="imageTabs"
        @close="handleFmClose"
        @file-selected="onFmFileSelected"
        @tab-created="onFmTabCreated"
        @tab-deleted="onFmTabDeleted"
      />

    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'pinia';
import { useGiayToStore } from '@/stores/giayToStore';
import api from '@/api';
import FileManagerModal from '@/components/FileManagerModal.vue';
import {
  isImage, isPdf, isDoc, isSheet, isSlide, isVideo, isAudio, isArchive, isText,
  fileLabel, fileIconClass, openInNewTab
} from '@/utils/fileTypeUtils';

// --- Enum hiển thị ---
const LOAI_GIAY_TO_XE = [
  { value: 'DANG_KY_XE', text: 'Đăng ký xe (Cà vẹt)' },
  { value: 'DANG_KIEM', text: 'Đăng kiểm' },
  { value: 'BH_TNDS_BB', text: 'Bảo hiểm TNDS BB' },
  { value: 'BH_VAT_CHAT', text: 'Bảo hiểm Vật chất' },
  { value: 'PHU_HIEU_KDVT', text: 'Phù hiệu KDVT' },
  { value: 'GIAY_PHEP_DAC_BIET', text: 'Giấy phép đặc biệt' },
];
const LOAI_GIAY_TO_TAI_XE = [
  { value: 'GPLX_B2', text: 'GPLX B2' },
  { value: 'GPLX_C', text: 'GPLX C' },
  { value: 'GPLX_FC', text: 'GPLX FC' },
  { value: 'CCCD', text: 'CCCD/CMND' },
  { value: 'CHUNG_CHI_NV', text: 'Chứng chỉ nghiệp vụ' },
  { value: 'GIAY_KHAM_SK', text: 'Giấy khám sức khỏe' },
];

const DEFAULT_FORM = {
  maLoaiGiayTo: null,
  tenHienThi: '',
  soGiayTo: '',
  ngayCap: null,
  ngayHetHan: null,
  noiCap: '',
  fileUrl: '',
  ghiChu: '',
  active: true,
  xeId: null,
  nguoiDungId: null,
};

export default {
  name: 'QuanLyGiayTo',
  components: { FileManagerModal },
  data() {
    return {
      showModal: false,
      isEdit: false,
      formError: null,
      form: { ...DEFAULT_FORM },
      ownerType: 'xe',

      showDelete: false,
      pendingDelete: null,

      // FileManager state
      showFileManagerModal: false,
      imageTabs: [],      // tabs => {id, name, folderName}
      fmTarget: 'file',   // chỉ có 1 target: fileUrl
      docPreview: { open: false, url: '', isBlob: false },

    };
  },

  computed: {
    ...mapState(useGiayToStore, [
      'isLoading', 'error', 'items', 'totalPages', 'totalElements', 'size',
      'xeOptions', 'taiXeOptions', 'isLoadingOptions',
    ]),
    ...mapGetters(useGiayToStore, ['currentPage']),
    pageSize: { get() { return this.size; }, set(_) {} },

    loaiGiayToXeOptions() { return LOAI_GIAY_TO_XE; },
    loaiGiayToTaiXeOptions() { return LOAI_GIAY_TO_TAI_XE; },
  },

  methods: {
    ...mapActions(useGiayToStore, [
      'fetchList', 'setPage', 'setSize',
      'createGiayTo', 'updateGiayTo', 'deleteGiayTo',
      'fetchOptionsIfNeeded',
    ]),
    isImage, isPdf, isDoc, isSheet, isSlide, isVideo, isAudio, isArchive, isText,
    fileLabel, fileIconClass, openInNewTab,

    // ==== File Manager helpers ====
    async fetchFolders() {
      try {
        const res = await api.get('/api/images/folders');
        let tabs = (res.data || []).map((folderName, idx) => ({
          id: String(idx + 1),
          name: folderName,
          folderName,
        }));
        // ƯU TIÊN chỉ hiển thị folder 'giay_to' nếu có
        const onlyDoc = tabs.find(t => t.folderName === 'giay_to');
        this.imageTabs = onlyDoc ? [onlyDoc] : tabs;
      } catch (e) {
        console.error('[GiayTo] Lỗi tải folders:', e);
        this.imageTabs = [];
      }
    },
    async openFileManager() {
      await this.fetchFolders();
      this.showFileManagerModal = true;
      document.body.style.overflow = 'hidden';
    },
    handleFmClose() {
      this.showFileManagerModal = false;
      if (!this.showModal) document.body.style.overflow = '';
    },
    onFmTabCreated(newTab) {
      // nếu đang chỉ hiển thị 'giay_to' và user tạo tab khác, vẫn thêm vào UI
      this.imageTabs.push(newTab);
    },
    onFmTabDeleted(tabId) {
      this.imageTabs = this.imageTabs.filter(t => t.id !== tabId);
    },
    onFmFileSelected(image) {
      if (!image?.url) return;
      this.form.fileUrl = image.url; // điền vào form
      this.handleFmClose();
    },

    // ==== Helpers ====
    formatDate(dateString) {
      if (!dateString) return '-';
      const d = new Date(dateString);
      if (isNaN(d.getTime())) return '-';
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yy = d.getFullYear();
      return `${dd}/${mm}/${yy}`;
    },
    isExpired(dateString) {
      if (!dateString) return false;
      const expiry = new Date(dateString);
      const today = new Date();
      expiry.setHours(0,0,0,0); today.setHours(0,0,0,0);
      return expiry < today;
    },
    isNearExpiry(dateString, days = 30) {
      if (!dateString || this.isExpired(dateString)) return false;
      const expiry = new Date(dateString);
      const diff = (expiry - new Date()) / (1000*60*60*24);
      return diff <= days;
    },

    // ==== Paging ====
    changeSize() { this.setSize(Number(this.pageSize)); this.fetchList(); },
    goPage(p) { if (p < 1 || p > this.totalPages) return; this.setPage(p); this.fetchList(); },

    // ==== Modal create/edit ====
    openCreate() {
      this.isEdit = false;
      this.formError = null;
      this.form = { ...DEFAULT_FORM };
      this.ownerType = 'xe';
      this.showModal = true;
      document.body.style.overflow = 'hidden';
      this.fetchOptionsIfNeeded();
    },
    openEdit(item) {
      this.isEdit = true;
      this.formError = null;
      this.form = {
        maLoaiGiayTo: item.maLoaiGiayTo,
        tenHienThi: item.tenHienThi,
        soGiayTo: item.soGiayTo,
        ngayCap: item.ngayCap || null,
        ngayHetHan: item.ngayHetHan || null,
        noiCap: item.noiCap,
        fileUrl: item.fileUrl,
        ghiChu: item.ghiChu,
        active: item.active,
        xeId: item.xeId,
        nguoiDungId: item.nguoiDungId,
      };
      this.form._id = item.id;
      this.ownerType = item.xeId ? 'xe' : 'taixe';
      this.showModal = true;
      document.body.style.overflow = 'hidden';
      this.fetchOptionsIfNeeded();
    },
    closeModal() {
      this.showModal = false;
      if (!this.showFileManagerModal) document.body.style.overflow = '';
    },

    updateTenHienThiDefault() {
      if (this.isEdit) return;
      const mapText = [...LOAI_GIAY_TO_XE, ...LOAI_GIAY_TO_TAI_XE]
        .reduce((m, it) => (m[it.value] = it.text, m), { KHAC: 'Giấy tờ khác' });
      const loaiText = mapText[this.form.maLoaiGiayTo] || '';
      let ownerText = '';
      if (this.ownerType === 'xe' && this.form.xeId) {
        const xe = this.xeOptions.find(x => x.id === this.form.xeId);
        ownerText = xe?.bienSo || '';
      } else if (this.ownerType === 'taixe' && this.form.nguoiDungId) {
        const tx = this.taiXeOptions.find(t => t.id === this.form.nguoiDungId);
        ownerText = tx?.hoTen || '';
      }
      this.form.tenHienThi = loaiText && ownerText ? `${loaiText} - ${ownerText}` : (loaiText || ownerText || '');
    },

    async submitForm() {
      this.formError = null;
      if (!this.form.maLoaiGiayTo) { this.formError = 'Vui lòng chọn Loại Giấy tờ.'; return; }
      if (this.ownerType === 'xe' && !this.form.xeId) { this.formError = 'Vui lòng chọn Xe.'; return; }
      if (this.ownerType === 'taixe' && !this.form.nguoiDungId) { this.formError = 'Vui lòng chọn Tài xế.'; return; }
      if (this.form.ngayCap && this.form.ngayHetHan && this.form.ngayHetHan < this.form.ngayCap) {
        this.formError = 'Ngày hết hạn không được trước Ngày cấp.'; return;
      }
      try {
        const payload = {
          maLoaiGiayTo: this.form.maLoaiGiayTo,
          tenHienThi: this.form.tenHienThi?.trim() || null,
          soGiayTo: this.form.soGiayTo?.trim() || null,
          ngayCap: this.form.ngayCap || null,
          ngayHetHan: this.form.ngayHetHan || null,
          noiCap: this.form.noiCap?.trim() || null,
          fileUrl: this.form.fileUrl?.trim() || null,
          ghiChu: this.form.ghiChu?.trim() || null,
          active: this.form.active,
          xeId: this.ownerType === 'xe' ? this.form.xeId : null,
          nguoiDungId: this.ownerType === 'taixe' ? this.form.nguoiDungId : null,
        };
        if (this.isEdit) await this.updateGiayTo(this.form._id, payload);
        else await this.createGiayTo(payload);
        this.closeModal();
      } catch (e) {
        this.formError = e?.response?.data?.message
          || (typeof e?.response?.data === 'string' ? e.response.data : null)
          || e?.message
          || 'Có lỗi xảy ra khi lưu giấy tờ.';
        console.error('[GiayTo] submit lỗi:', e);
      }
    },

    // Xóa
    confirmDelete(item) {
      this.pendingDelete = item;
      this.showDelete = true;
      document.body.style.overflow = 'hidden';
    },
    async doDelete() {
      if (!this.pendingDelete) return;
      try {
        await this.deleteGiayTo(this.pendingDelete.id);
        this.cancelDelete();
      } catch (e) {
        alert(e?.response?.data?.message || e?.message || 'Xoá thất bại.');
        console.error('[GiayTo] delete lỗi:', e);
      }
    },
    cancelDelete() {
      this.showDelete = false;
      this.pendingDelete = null;
      document.body.style.overflow = '';
    },

  },

  mounted() {
    this.fetchList();
  }
};
</script>

<style scoped>
.page-wrapper {}
.table td, .table th { vertical-align: middle; }
.modal { overflow-y: auto; }
.text-danger { font-weight: bold; }
.text-warning { color: #ffc107 !important; }
.btn-outline-light.text-muted { cursor: not-allowed; }
optgroup { font-weight: bold; font-style: italic; }
.file-chip {
  width: 56px;
  height: 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #dee2e6;
  border-radius: 6px;
}
.file-chip i {
  font-size: 18px;
}

</style>
